import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  FileCheck2,
  Package,
  TrendingUp,
  Boxes,
  Megaphone,
  ShieldCheck,
  CheckCircle2,
  Clock,
  MapPin,
} from "lucide-react";

import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { PlatformBadge } from "./components/PlatformBadge";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { submitLeadWithWhatsApp } from "./lib/whatsappHandoff";
import { toast } from "./components/ui/sonner";

/* -----------------------------------------------------------------------------
 * /zepto-seller-onboarding
 *
 * Deep, indexable platform page. Replaces the previous <SeoStubPage /> stub
 * that carried `robots: noindex, nofollow`. Emits Service + FAQPage JSON-LD,
 * self-referencing canonical, and hands off to our shared WhatsApp lead
 * pipeline (silent backend POST + wa.me open).
 *
 * Positioning throughout: PickQuick operates brands on Zepto end to end,
 * city by city.
 * ---------------------------------------------------------------------------*/

const CANONICAL = "https://letspickquick.com/zepto-seller-onboarding";
const WHATSAPP_NUMBER = "918733085863";
const CALENDAR_LINK = "https://calendar.app.google/q771ocWtwv6E75dr5";

const FAQS = [
  {
    q: "How long does Zepto onboarding take?",
    a: "Typically 7 to 14 days with PickQuick managing it, subject to category and documents.",
  },
  {
    q: "What documents are needed to sell on Zepto?",
    a: "GST, PAN, business registration, brand trademark, FSSAI (food and grocery), bank details, product images.",
  },
  {
    q: "What commission does Zepto charge?",
    a: "It varies by category. We will walk you through the exact commercials for your SKUs on a free audit call.",
  },
  {
    q: "Do you handle city-level pricing and ops?",
    a: "Yes. We resolve pricing and inventory to the dark-store catchment level.",
  },
  {
    q: "Can you manage growth after launch?",
    a: "Yes, we operate the full lifecycle, not just onboarding.",
  },
];

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Zepto seller onboarding and growth, operated by PickQuick",
  serviceType: "Quick commerce seller onboarding, catalog, pricing, ads and growth on Zepto",
  provider: {
    "@type": "Organization",
    name: "PickQuick",
    legalName: "Civicgrid Technologies Pvt. Ltd.",
    url: "https://letspickquick.com",
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "sales",
      name: "Anuj Sahu",
      email: "anuj@letspickquick.com",
      telephone: "+91-8733085863",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    }],
  },
  areaServed: { "@type": "Country", "name": "India" },
  url: CANONICAL,
  description:
    "PickQuick handles Zepto seller registration, brand approval, city-level dark-store catalog, pincode pricing and pack strategy, inventory and fill-rate, ads and account health for FMCG brands. Operated end to end, not advised.",
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", "text": a },
  })),
};

const EyebrowLabel = ({ children }) => (
  <p
    className="text-xs font-semibold uppercase mb-4"
    style={{ color: "#F1592B", letterSpacing: "0.18em" }}
  >
    {children}
  </p>
);

const SectionHeading = ({ children, className = "" }) => (
  <h2
    className={`text-white font-semibold ${className}`}
    style={{
      fontSize: "clamp(30px, 3.4vw, 46px)",
      lineHeight: 1.12,
      letterSpacing: "-0.02em",
      maxWidth: "980px",
    }}
  >
    {children}
  </h2>
);

const BodyParagraph = ({ children, className = "" }) => (
  <p
    className={className}
    style={{ color: "#B8B8B8", fontSize: "17px", lineHeight: 1.65, maxWidth: "820px" }}
  >
    {children}
  </p>
);

const Card = ({ children, warm = false }) => (
  <div
    className="rounded-2xl p-6 md:p-7 h-full flex flex-col"
    style={{
      backgroundColor: warm ? "#1A0B05" : "#1A1A1A",
      border: "1px solid #262626",
    }}
  >
    {children}
  </div>
);

const CardIcon = ({ Icon }) => (
  <div
    className="w-11 h-11 rounded-lg mb-5 flex items-center justify-center"
    style={{ backgroundColor: "rgba(241,89,43,0.14)" }}
    aria-hidden="true"
  >
    <Icon className="w-5 h-5" style={{ color: "#F1592B" }} />
  </div>
);

const CardTitle = ({ children }) => (
  <h3
    className="text-white mb-3"
    style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em" }}
  >
    {children}
  </h3>
);

const CardBody = ({ children }) => (
  <p style={{ color: "#B8B8B8", fontSize: "14.5px", lineHeight: 1.6 }}>{children}</p>
);

const CheckRow = ({ children }) => (
  <li className="flex items-start gap-3" style={{ color: "#E8E8E8", fontSize: "15px", lineHeight: 1.55 }}>
    <CheckCircle2 className="mt-0.5 w-5 h-5 flex-shrink-0" style={{ color: "#F1592B" }} aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const ZeptoOnboarding = () => {
  useDocumentMeta({
    title: "Zepto Seller Onboarding & Growth | PickQuick",
    description:
      "Launch and scale on Zepto with PickQuick. City-level dark-store ops, pincode demand mapping, pricing and growth for FMCG brands. Book a free audit.",
    ogType: "website",
    ogUrl: CANONICAL,
    canonical: CANONICAL,
    keywords: "zepto seller onboarding, sell on zepto, zepto registration, zepto dark store, zepto ads, zepto commission, quick commerce onboarding, FMCG zepto, PickQuick",
    schema: [SERVICE_SCHEMA, FAQ_SCHEMA],
  });

  const [form, setForm] = useState({
    name: "",
    brand: "",
    email: "",
    phone: "",
    category: "",
  });

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const sendOnWhatsApp = useCallback(() => {
    if (!form.name.trim() || !form.brand.trim() || !form.email.trim()) {
      toast.error("Name, brand and work email are required.");
      return;
    }

    submitLeadWithWhatsApp({
      payload: {
        name: form.name.trim(),
        brand: form.brand.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        product_category: form.category.trim() || "(not provided)",
        context: "Interested in Zepto onboarding and growth",
        lead_type: "zepto-onboarding",
        source: "zepto-page-form",
      },
      whatsapp: {
        title: "New PickQuick Zepto onboarding enquiry",
        source: "zepto-page-form",
        pagePath: "/zepto-seller-onboarding",
        fields: {
          Name: form.name.trim(),
          Brand: form.brand.trim(),
          "Work email": form.email.trim(),
          Phone: form.phone.trim() || "(not provided)",
          "Product category": form.category.trim() || "(not provided)",
        },
      },
    });

    toast.success("Opening WhatsApp with your details, we will pick up from there.");
    setForm({ name: "", brand: "", email: "", phone: "", category: "" });
  }, [form]);

  const onFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendOnWhatsApp();
    }
  };

  const scrollToContact = () => {
    const el = document.getElementById("zepto-contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const inputStyle = {
    width: "100%",
    height: "46px",
    padding: "0 14px",
    backgroundColor: "#0F0F0F",
    border: "1px solid #262626",
    borderRadius: "8px",
    color: "#FFFFFF",
    fontSize: "15px",
    outline: "none",
  };

  const primaryCtaStyle = {
    backgroundColor: "#F1592B",
    color: "#FFFFFF",
    height: "52px",
    padding: "0 26px",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    borderRadius: "10px",
    fontSize: "15.5px",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
  };

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-white flex flex-col">
      <SiteHeader />

      {/* Floating WhatsApp button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with PickQuick on WhatsApp"
        className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:shadow-xl transition-shadow duration-200"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </a>

      <main className="flex-1">
        {/* ============ SECTION 1: HERO ============ */}
        <section
          className="pt-24 pb-24 md:pt-28 md:pb-28 relative overflow-hidden"
          style={{ background: "#000000" }}
          data-testid="zepto-hero"
        >
          <div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(241,89,43,0.10) 0%, transparent 70%)" }}
            aria-hidden="true"
          />
          <div className="container relative z-10">
            <div className="max-w-[1080px]">
              <div className="mb-6">
                <PlatformBadge id="zepto" size="hero" />
              </div>
              <EyebrowLabel>Zepto for brands</EyebrowLabel>
              <h1
                className="font-bold text-white"
                data-testid="zepto-h1"
                style={{ fontSize: "clamp(34px, 5.2vw, 68px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}
              >
                Zepto seller onboarding and{" "}
                <span style={{ color: "#F1592B" }}>growth</span> for FMCG brands.
              </h1>
              <p
                className="mt-7 max-w-[820px]"
                style={{ color: "#B8B8B8", fontSize: "18px", lineHeight: 1.65 }}
              >
                Launch on Zepto and win city by city. We handle registration, catalog, city-level pricing and fill-rate, and run it as an operator.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={scrollToContact}
                  data-testid="zepto-hero-cta"
                  style={primaryCtaStyle}
                >
                  Book a free 30 minute audit call
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 2: WHAT IT IS ============ */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="zepto-what-it-is">
          <div className="container">
            <EyebrowLabel>What it is</EyebrowLabel>
            <SectionHeading>
              What Zepto seller <span style={{ color: "#F1592B" }}>onboarding</span> means.
            </SectionHeading>
            <div className="mt-8 grid gap-8 md:grid-cols-2 max-w-[1180px]">
              <BodyParagraph>
                Zepto seller onboarding is the process of registering your brand on Zepto, one of India&apos;s leading 10 minute delivery platforms.
              </BodyParagraph>
              <BodyParagraph>
                Zepto&apos;s demand is intensely hyperlocal, the same product sells differently street to street and city to city, which makes pincode-level pricing and pack decisions the difference between winning and stocking out. PickQuick manages registration through go-live, then operates the account.
              </BodyParagraph>
            </div>
          </div>
        </section>

        {/* ============ SECTION 3: WHAT WE DO ON ZEPTO ============ */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="zepto-what-we-do">
          <div className="container">
            <EyebrowLabel>What we do on Zepto</EyebrowLabel>
            <SectionHeading>
              We <span style={{ color: "#F1592B" }}>operate</span> your brand on Zepto. City by city.
            </SectionHeading>
            <BodyParagraph className="mt-5">
              Six operating pillars, run by PickQuick as your Zepto team on the ground.
            </BodyParagraph>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: FileCheck2,
                  title: "Registration and Brand Approval",
                  body: "Full seller registration and brand approval, documentation to activation.",
                },
                {
                  icon: Package,
                  title: "City-Level Dark-Store Catalog",
                  body: "Catalog built to Zepto's guidelines, tuned to each city's dark-store network.",
                },
                {
                  icon: MapPin,
                  title: "Pincode Pricing and Pack",
                  body: "Right price and pack per micro-market, matched to how each catchment actually buys.",
                },
                {
                  icon: Boxes,
                  title: "Inventory and Fill-Rate",
                  body: "The right stock in the right city dark stores so nothing dies on the shelf.",
                },
                {
                  icon: Megaphone,
                  title: "Ads and Visibility",
                  body: "Search and sponsored visibility so your SKUs surface where demand is.",
                },
                {
                  icon: ShieldCheck,
                  title: "Account Health",
                  body: "Ongoing ops, reconciliation and issue resolution.",
                },
              ].map((c) => (
                <Card key={c.title}>
                  <CardIcon Icon={c.icon} />
                  <CardTitle>{c.title}</CardTitle>
                  <CardBody>{c.body}</CardBody>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SECTION 4: REQUIREMENTS ============ */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="zepto-requirements">
          <div className="container">
            <EyebrowLabel>Requirements</EyebrowLabel>
            <SectionHeading>
              What you <span style={{ color: "#F1592B" }}>need</span> to go live on Zepto.
            </SectionHeading>
            <BodyParagraph className="mt-5">
              PickQuick assembles and submits everything. Here is the checklist so you know what to line up on your end.
            </BodyParagraph>

            <div className="mt-12 grid gap-5 md:grid-cols-2 max-w-[980px]">
              <Card>
                <ul className="space-y-4">
                  <CheckRow>GST Registration</CheckRow>
                  <CheckRow>PAN Card</CheckRow>
                  <CheckRow>Business Registration</CheckRow>
                  <CheckRow>Brand Trademark</CheckRow>
                </ul>
              </Card>
              <Card>
                <ul className="space-y-4">
                  <CheckRow>FSSAI License, mandatory for food and grocery</CheckRow>
                  <CheckRow>Bank Account Details</CheckRow>
                  <CheckRow>Professional Product Images</CheckRow>
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* ============ SECTION 5: TIMELINE ============ */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="zepto-timeline">
          <div className="container">
            <EyebrowLabel>Timeline</EyebrowLabel>
            <SectionHeading>
              How <span style={{ color: "#F1592B" }}>fast</span> you go live.
            </SectionHeading>
            <div className="mt-10 max-w-[980px]">
              <Card>
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(241,89,43,0.14)" }}
                    aria-hidden="true"
                  >
                    <Clock className="w-6 h-6" style={{ color: "#F1592B" }} />
                  </div>
                  <div>
                    <p className="text-white" style={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-0.01em" }}>
                      <span style={{ color: "#F1592B" }}>7 to 14 days</span>, typically.
                    </p>
                    <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "16px", lineHeight: 1.65 }}>
                      Zepto seller approval typically takes 7 to 14 days with PickQuick managing the process end to end, depending on category and documentation readiness.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* ============ SECTION 6: COMMERCIALS ============ */}
        <section className="py-24" style={{ backgroundColor: "#1A0B05" }} data-testid="zepto-commercials">
          <div className="container">
            <EyebrowLabel>Commercials</EyebrowLabel>
            <SectionHeading>
              What Zepto <span style={{ color: "#F1592B" }}>costs</span>, and how we are paid.
            </SectionHeading>
            <div className="mt-8 grid gap-8 md:grid-cols-2 max-w-[1180px]">
              <BodyParagraph>
                Zepto&apos;s commission and commercial terms vary by category, and your real growth math depends on your SKUs, pricing and margins.
              </BodyParagraph>
              <BodyParagraph>
                Instead of a generic figure, we run a free audit of your top SKUs and walk you through the exact commercials and the city-level opportunity on your call. PickQuick is paid on the growth we create, never a retainer.
              </BodyParagraph>
            </div>
            <div className="mt-10">
              <button
                type="button"
                onClick={scrollToContact}
                data-testid="zepto-commercials-cta"
                style={primaryCtaStyle}
              >
                Book a free audit to see your Zepto commercials
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* ============ SECTION 7: WHY PICKQUICK ============ */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="zepto-why">
          <div className="container">
            <EyebrowLabel>Why PickQuick</EyebrowLabel>
            <SectionHeading>
              Pincode-level <span style={{ color: "#F1592B" }}>operators</span>, not advisors.
            </SectionHeading>
            <div className="mt-8 max-w-[900px]">
              <BodyParagraph>
                We run brands on Zepto every day, operating pricing, inventory, ads and restocking across live city dark stores. Our pincode-level demand mapping is exactly where regional brands beat national giants.
              </BodyParagraph>
              <BodyParagraph className="mt-5">
                We operate brands including Gokul, Nandini and Aavin in dairy and Goldiee and Ramdev in masala.
              </BodyParagraph>
            </div>

            {/* Internal SEO cross-links */}
            <div className="mt-12 grid gap-4 md:grid-cols-2 max-w-[900px]">
              <Link
                to="/qc-consulting"
                data-testid="zepto-link-qc-consulting"
                className="rounded-xl p-5 group"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626", textDecoration: "none" }}
              >
                <p style={{ color: "#F1592B", fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  QC Consulting
                </p>
                <p className="mt-2 text-white" style={{ fontSize: "16px", fontWeight: 600 }}>
                  Need a computed operating plan?
                </p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
                  PickQuick Intelligence, our demand-decision engine for Zepto, Blinkit and Instamart.
                </p>
              </Link>
              <Link
                to="/for-platforms"
                data-testid="zepto-link-for-platforms"
                className="rounded-xl p-5 group"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626", textDecoration: "none" }}
              >
                <p style={{ color: "#F1592B", fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  For Platforms
                </p>
                <p className="mt-2 text-white" style={{ fontSize: "16px", fontWeight: 600 }}>
                  Are you a platform team?
                </p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
                  Regional brand depth and consolidated monetisation for quick commerce platforms.
                </p>
              </Link>
              <Link
                to="/guides/how-to-sell-on-zepto"
                data-testid="zepto-link-how-to-sell"
                className="rounded-xl p-5 group"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626", textDecoration: "none" }}
              >
                <p style={{ color: "#F1592B", fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  Guide
                </p>
                <p className="mt-2 text-white" style={{ fontSize: "16px", fontWeight: 600 }}>
                  How to sell on Zepto as a brand
                </p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
                  A deeper walkthrough of the process, from paperwork to shelf.
                </p>
              </Link>
              <Link
                to="/guides/blinkit-vs-zepto"
                data-testid="zepto-link-vs-blinkit"
                className="rounded-xl p-5 group"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626", textDecoration: "none" }}
              >
                <p style={{ color: "#F1592B", fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  Guide
                </p>
                <p className="mt-2 text-white" style={{ fontSize: "16px", fontWeight: 600 }}>
                  Blinkit vs Zepto for FMCG sellers
                </p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
                  Where each platform wins, from PickQuick&apos;s operators.
                </p>
              </Link>
            </div>
          </div>
        </section>

        {/* ============ SECTION 8: FAQ ============ */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="zepto-faq">
          <div className="container">
            <EyebrowLabel>FAQ</EyebrowLabel>
            <SectionHeading>
              Zepto onboarding, <span style={{ color: "#F1592B" }}>answered</span>.
            </SectionHeading>

            <div className="mt-12 grid gap-4 max-w-[900px]">
              {FAQS.map((f, i) => (
                <details
                  key={f.q}
                  data-testid={`zepto-faq-${i}`}
                  className="rounded-xl p-5"
                  style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                >
                  <summary
                    className="cursor-pointer text-white"
                    style={{ fontSize: "16px", fontWeight: 600, listStyle: "none" }}
                  >
                    {f.q}
                  </summary>
                  <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.65 }}>
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SECTION 9: CONTACT ============ */}
        <section
          id="zepto-contact"
          className="py-24"
          style={{ backgroundColor: "#000000" }}
          data-testid="zepto-contact"
        >
          <div className="container">
            <EyebrowLabel>Contact</EyebrowLabel>
            <SectionHeading>
              Ready to get live on <span style={{ color: "#F1592B" }}>Zepto</span>?
            </SectionHeading>
            <BodyParagraph className="mt-5">
              Share your details and we will open WhatsApp with the same message pre-filled. We reply within one business day.
            </BodyParagraph>

            <div className="mt-12 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] max-w-[1180px]">
              <div
                className="rounded-2xl p-6 md:p-7"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="zp-name" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Name *
                    </label>
                    <input
                      id="zp-name"
                      data-testid="zepto-form-name"
                      value={form.name}
                      onChange={set("name")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="zp-brand" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Brand *
                    </label>
                    <input
                      id="zp-brand"
                      data-testid="zepto-form-brand"
                      value={form.brand}
                      onChange={set("brand")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="Brand name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="zp-email" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Work email *
                    </label>
                    <input
                      id="zp-email"
                      data-testid="zepto-form-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="you@brand.com"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="zp-phone" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Phone
                    </label>
                    <input
                      id="zp-phone"
                      data-testid="zepto-form-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="With country code"
                      style={inputStyle}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="zp-category" className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
                      Product category
                    </label>
                    <input
                      id="zp-category"
                      data-testid="zepto-form-category"
                      value={form.category}
                      onChange={set("category")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="Dairy, masala, snacks, home care..."
                      style={inputStyle}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={sendOnWhatsApp}
                  data-testid="zepto-form-submit"
                  className="mt-6 inline-flex items-center gap-2"
                  style={{
                    ...primaryCtaStyle,
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <MessageCircle className="w-5 h-5" />
                  Send on WhatsApp
                </button>
                <p className="mt-3" style={{ color: "#8A8A8A", fontSize: "13px" }}>
                  Opens WhatsApp with your details pre-filled. We reply within one business day.
                </p>
              </div>

              <div
                className="rounded-2xl p-6 md:p-7"
                style={{ backgroundColor: "#1A0B05", border: "1px solid #262626" }}
              >
                <p className="text-white" style={{ fontSize: "16px", fontWeight: 600 }}>
                  Talk directly to PickQuick
                </p>
                <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.6 }}>
                  Anuj Sahu, single point of contact for Zepto onboarding.
                </p>
                <div className="mt-5 space-y-3" style={{ fontSize: "14px" }}>
                  <div>
                    <span style={{ color: "#8A8A8A", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.1em" }}>Email</span>
                    <div className="mt-1">
                      <a href="mailto:anuj@letspickquick.com" data-testid="zepto-contact-email" style={{ color: "#F1592B" }}>
                        anuj@letspickquick.com
                      </a>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: "#8A8A8A", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.1em" }}>Phone / WhatsApp</span>
                    <div className="mt-1">
                      <a href="tel:+918733085863" data-testid="zepto-contact-phone" style={{ color: "#F1592B" }}>
                        +91 8733085863
                      </a>
                    </div>
                  </div>
                  <div className="pt-2">
                    <a
                      href={CALENDAR_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="zepto-contact-calendar"
                      className="inline-flex items-center gap-2"
                      style={{ color: "#F1592B", fontSize: "14px", fontWeight: 600 }}
                    >
                      Or grab a slot on the calendar
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ZeptoOnboarding;
